var React = require('react');
var reactValidate = require('./ReactValidationNodePackageCustomized.js');
var selectableDataFunctions = require('../selectableDataFunctions.jsx');

const getSelectableOptions = function(props) {
    return props.selectdatafunction ? props.selectdatafunction(props) : selectableDataFunctions.getSelectableOption(props);
};

const getInputWrapperClass = function(error, isUsed) {
    var message = "validation-input";

    if (error && isUsed) {
        return message + " has-error";
    }

    if (error) {
        return message + " has-error unused";
    }

    return error;
};

const getValidationMarkup = function(error, isUsed) {
    return error ? <span className={isUsed ? "has-error" : "has-error unused"}>{error}</span> : null;
};

 //Must include all of these params, so that they are available to child components in the form
const Form = ({ getValues, validate, validateAll, showError, hideError, children, ...props }) => (
    <form {...props}>{children}</form>
);

const Input = ({ error, isChanged, isUsed, ...props }) => {
    return <div className={getInputWrapperClass(error, isUsed)}>
                <input {...props}/>
                {getValidationMarkup(error, isUsed)}
           </div>;
};

const RadioButtons = ({ error, isChanged, isUsed, ...props }) => {
    var radioButtons = props.uselabels ? selectableDataFunctions.getRadioButtonOptionsWithLabels(props) : selectableDataFunctions.getRadioButtonOptions(props);
    radioButtons = props.selectdatafunction ? getSelectableOptions(props) : radioButtons;

    return <div className={getInputWrapperClass(error, isUsed) + ' validation-radio-buttons'}>
        {radioButtons}
        {getValidationMarkup(error, isUsed)}
    </div>;
};

const TextArea = ({ error, isChanged, isUsed, ...props }) => {
    return <div className={getInputWrapperClass(error, isUsed)}>
        <textarea rows="10" id="bio" {...props} ></textarea>
        {getValidationMarkup(error, isUsed)}
    </div>;
};

const getSpinnerClass = (showspinner) => (showspinner ? ' show-spinner' : '');

const Button = ({ hasErrors, showspinner, defaultclass, ismodified, isvalid, ...props }) => {
    // showspinner carries getDisabledUiClass()'s result ('' or 'ui-is-disabled'), which
    // reflects an in-flight save. Without disabling on it too, a second click/tap before
    // the response lands re-submits the same record and can create a duplicate downstream.
    var isSaving = !!showspinner;

    var classes = hasErrors || ismodified === "false" || isvalid === "false"
        ? defaultclass + ' disabled'
        : defaultclass;

    classes += getSpinnerClass(showspinner);

    return (
        <button className={classes} {...props} disabled={hasErrors || ismodified === "false" || isSaving} />
    );
};

// A native <input type="date"> accumulates the digits you type into a segment (year:
// 2 -> 20 -> 202 -> 2020) in an internal buffer. That buffer is discarded whenever React
// re-assigns the element's `value` while you're editing -- and this field does exactly
// that on every keystroke (the validation wrapper re-renders with the framework value, and
// callers such as Demographics push a moment-reformatted value back via onInput). The reset
// made a 4-digit year impossible to type (2020 collapsed to 0000). To fix it we run the
// input UNCONTROLLED while it holds focus and only push the external value into the DOM when
// the user is not actively editing.
class DatePickerInput extends React.Component {
    constructor(props) {
        super(props);
        this.node = null;
        this.isEditing = false;
        this.setNode = this.setNode.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }

    componentDidMount() {
        this.syncFromProps();
    }

    componentDidUpdate() {
        if (!this.isEditing) {
            this.syncFromProps();
        }
    }

    setNode(node) {
        this.node = node;
    }

    syncFromProps() {
        if (!this.node) return;
        var next = this.props.value == null ? "" : this.props.value;
        if (this.node.value !== next) {
            this.node.value = next;
        }
    }

    handleFocus(e) {
        this.isEditing = true;
        if (this.props.onFocus) this.props.onFocus(e);
    }

    handleBlur(e) {
        this.isEditing = false;
        if (this.props.onBlur) this.props.onBlur(e);
    }

    render() {
        var { isChanged, isUsed, error, value, onFocus, onBlur, ...props } = this.props;
        // Cap native date inputs to 4-digit years: Chrome otherwise lets the year
        // segment grow to 6 digits. A max with a 4-digit year constrains it (every
        // real birthdate / certification date falls well under 9999). The max
        // attribute is always yyyy-mm-dd regardless of the display date-format.
        if (props.type === "date" && props.max == null) {
            props.max = "9999-12-31";
        }
        return <div className={getInputWrapperClass(error, isUsed)}>
            <input {...props}
                ref={this.setNode}
                defaultValue={value == null ? "" : value}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur} />
            {getValidationMarkup(error, isUsed)}
        </div>;
    }
}

// isChanged needs to be passed in here, or React will give an unknown prop warning
const SimpleSelect = ({ isChanged, error, isUsed, ...props }) => {
    const options = getSelectableOptions(props);

    delete props.selectdatafunction;
    return <div className={getInputWrapperClass(error, isUsed)}>
        <select onChange={props.onChange} name={props.name} value={props.value} data-recordid={props.recordid}{...props}>
            <option value="">Select</option>
            {options}
        </select>
        {getValidationMarkup(error, isUsed)}
    </div>;
};

const NextStepButton = ({ hasErrors, showspinner, defaultclass, ismodified, isvalid, ...props }) => {
    const disabled = hasErrors || isvalid === "false";
    return (
        <button {...props} className={(disabled ? 'disabled' : '') + getSpinnerClass(showspinner)} disabled={disabled} tabIndex="0" type="submit" value="Submit"/>
    );
};

const RadioButtonsWrapped = reactValidate.control(RadioButtons);
const RecordFormWrapped = reactValidate.form(Form);
const RecordInputWrapped = reactValidate.control(Input);
const SubmitButtonWrapped = reactValidate.button(Button);
const DropdownSelectWrapped = reactValidate.control(SimpleSelect);
const DatePickerWrapped = reactValidate.control(DatePickerInput);
const TextAreaWrapped = reactValidate.control(TextArea);

const MemberJoinNextButton = reactValidate.button(NextStepButton);

module.exports = {
    $form: RecordFormWrapped,
    $input: RecordInputWrapped,
    $button: SubmitButtonWrapped,
    $radioButtons: RadioButtonsWrapped,
    $simpleSelect: DropdownSelectWrapped,
    $datePicker: DatePickerWrapped,
    $textArea: TextAreaWrapped,
    $nextButton: MemberJoinNextButton
};